class GizmoAiAssistant extends HTMLElement {
  connectedCallback() {
    this.endpoint = window.GizmoAssistantEndpoint || this.dataset.endpoint || '/api/gizmo-assistant';
    this.launcher = this.querySelector('.gizmo-ai-assistant__launcher');
    this.panel = this.querySelector('.gizmo-ai-assistant__panel');
    this.closeButton = this.querySelector('.gizmo-ai-assistant__close');
    this.form = this.querySelector('[data-gizmo-ai-form]');
    this.input = this.querySelector('.gizmo-ai-assistant__input');
    this.messages = this.querySelector('[data-gizmo-ai-messages]');
    this.prompts = this.querySelector('[data-gizmo-ai-prompts]');

    this.launcher?.addEventListener('click', () => this.toggle());
    this.closeButton?.addEventListener('click', () => this.close());
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.prompts?.addEventListener('click', (event) => this.onPromptClick(event));
    this.messages?.addEventListener('click', (event) => this.onPromptClick(event));
    this.addEventListener('click', (event) => this.onProductClick(event));
  }

  context() {
    return {
      pageType: this.dataset.pageType || '',
      productHandle: this.dataset.productHandle || '',
      productTitle: this.dataset.productTitle || '',
      deviceCompatibility: this.dataset.deviceCompatibility || '',
      productType: this.dataset.productType || '',
      designStyle: this.dataset.designStyle || '',
      franchise: this.dataset.franchise || '',
      inspiration: this.dataset.inspiration || ''
    };
  }

  toggle() {
    if (this.panel.hidden) {
      this.panel.hidden = false;
      this.launcher?.setAttribute('aria-expanded', 'true');
      setTimeout(() => this.input?.focus(), 50);
    } else {
      this.close();
    }
  }

  close() {
    this.panel.hidden = true;
    this.launcher?.setAttribute('aria-expanded', 'false');
  }

  onPromptClick(event) {
    const button = event.target.closest('[data-prompt]');
    if (!button) return;
    this.ask(button.dataset.prompt);
  }

  onSubmit(event) {
    event.preventDefault();
    const message = this.input.value.trim();
    if (!message) return;
    this.input.value = '';
    this.ask(message);
  }

  async ask(message) {
    this.addMessage(message, 'user');
    const loading = this.addMessage('Finding real matches...', 'assistant', true);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          context: this.context()
        })
      });

      if (!response.ok) throw new Error(`Assistant request failed: ${response.status}`);

      const data = await response.json();
      loading.remove();
      this.renderResponse(data);
    } catch (error) {
      loading.remove();
      this.addMessage('I could not load recommendations right now. Try the Find Your Skin guide or search by device.', 'assistant');
    }
  }

  addMessage(text, role = 'assistant', loading = false) {
    const message = document.createElement('div');
    message.className = `gizmo-ai-assistant__message gizmo-ai-assistant__message--${role}`;
    if (loading) message.classList.add('is-loading');
    message.textContent = text;
    this.messages.appendChild(message);
    this.messages.scrollTop = this.messages.scrollHeight;
    return message;
  }

  renderResponse(data) {
    this.addMessage(data.reply || 'Here are the best matches I found.', 'assistant');

    if (Array.isArray(data.products) && data.products.length) {
      const list = document.createElement('div');
      list.className = 'gizmo-ai-assistant__products';
      list.innerHTML = data.products.map((product) => this.productCard(product)).join('');
      this.messages.appendChild(list);
    }

    if (data.collectionUrl) {
      const link = document.createElement('a');
      link.className = 'gizmo-ai-assistant__collection-link';
      link.href = data.collectionUrl;
      link.dataset.gizmoAssistantClick = 'collection';
      link.textContent = 'Browse all matching skins';
      this.messages.appendChild(link);
    }

    if (Array.isArray(data.followUps) && data.followUps.length) {
      const followUps = document.createElement('div');
      followUps.className = 'gizmo-ai-assistant__followups';
      followUps.innerHTML = data.followUps.slice(0, 4).map((prompt) => (
        `<button type="button" data-prompt="${this.escapeAttr(prompt.prompt || prompt)}">${this.escapeHtml(prompt.label || prompt)}</button>`
      )).join('');
      this.messages.appendChild(followUps);
    }

    this.messages.scrollTop = this.messages.scrollHeight;
  }

  productCard(product) {
    const image = product.image ? `<img src="${this.escapeAttr(product.image)}" alt="">` : '';
    return `
      <a class="gizmo-ai-assistant__product" href="${this.escapeAttr(product.url)}" data-gizmo-assistant-click="product" data-product-handle="${this.escapeAttr(product.handle || '')}">
        <span class="gizmo-ai-assistant__product-image">${image}</span>
        <span class="gizmo-ai-assistant__product-copy">
          <strong>${this.escapeHtml(product.title || 'Product')}</strong>
          <span>${this.escapeHtml(product.reason || '')}</span>
        </span>
      </a>
    `;
  }

  onProductClick(event) {
    const target = event.target.closest('[data-gizmo-assistant-click]');
    if (!target) return;

    const payload = {
      type: target.dataset.gizmoAssistantClick,
      handle: target.dataset.productHandle || '',
      url: target.getAttribute('href') || '',
      pageType: this.dataset.pageType || '',
      sourceProductHandle: this.dataset.productHandle || '',
      timestamp: new Date().toISOString()
    };

    try {
      const events = JSON.parse(localStorage.getItem('gizmoAssistantClicks') || '[]');
      events.push(payload);
      localStorage.setItem('gizmoAssistantClicks', JSON.stringify(events.slice(-50)));
    } catch (error) {
      // Ignore storage failures; navigation should never be blocked by analytics.
    }

    if (window.navigator?.sendBeacon) {
      try {
        window.navigator.sendBeacon(this.clickEndpoint(), JSON.stringify(payload));
      } catch (error) {
        // Recommendation clicks should never block the shopper.
      }
    }
  }

  clickEndpoint() {
    return this.endpoint.replace(/\/api\/gizmo-assistant\/?$/, '/api/gizmo-assistant/click');
  }

  escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }

  escapeAttr(value) {
    return this.escapeHtml(value);
  }
}

if (!customElements.get('gizmo-ai-assistant')) {
  customElements.define('gizmo-ai-assistant', GizmoAiAssistant);
}
